import React, { useState, useEffect } from 'react';
import { Settings as SettingsIcon, Database, Zap, Globe, Save, RotateCcw, Download, Upload, FileText } from 'lucide-react';
import Card from '../components/Shared/Card';
import Button from '../components/Shared/Button';
import SettingsService from '../services/settingsService';
import config from '../config';

const Settings = () => {
  const [prompts, setPrompts] = useState(() => {
    const loaded = SettingsService.getPrompts();
    console.log('📝 Loaded prompts from localStorage:', loaded);
    return loaded;
  });
  const [preferences, setPreferences] = useState(() => {
    const loaded = SettingsService.getPreferences();
    console.log('⚙️ Loaded preferences from localStorage:', loaded);
    return loaded;
  });
  const [activeTab, setActiveTab] = useState('prompts'); // prompts, preferences, system
  const [saveMessage, setSaveMessage] = useState('');

  // Show if settings were loaded from localStorage on mount
  useEffect(() => {
    const storedSettings = localStorage.getItem('linkedin_post_generator_settings');
    if (storedSettings) {
      console.log('✅ Settings found in localStorage');
    } else {
      console.log('⚠️ No saved settings found - using defaults');
    }
  }, []);

  const systemInfo = [
    { label: 'منفذ الواجهة', value: window.location.port || '5175', icon: <Globe size={20} /> },
    { label: 'رابط الخادم', value: config.staticUrl, icon: <Database size={20} /> },
    { label: 'نموذج الذكاء الاصطناعي', value: 'Gemini 2.0-flash', icon: <Zap size={20} /> },
    { label: 'قاعدة البيانات', value: 'SQLite (Local)', icon: <Database size={20} /> }
  ];

  const handleSavePrompts = () => {
    if (SettingsService.savePrompts(prompts)) {
      console.log('✅ Prompts saved to localStorage');
      console.log('📦 localStorage data:', localStorage.getItem('linkedin_post_generator_settings'));
      setSaveMessage('✅ تم حفظ القوالب بنجاح!');
      setTimeout(() => setSaveMessage(''), 5000);
    } else {
      setSaveMessage('❌ فشل في حفظ القوالب');
    }
  };

  const handleResetPrompts = () => {
    if (window.confirm('إعادة تعيين جميع القوالب للافتراضي؟ لا يمكن التراجع عن هذا الإجراء.')) {
      const defaultPrompts = SettingsService.getDefaultPrompts();
      setPrompts(defaultPrompts);
      SettingsService.savePrompts(defaultPrompts);
      setSaveMessage('تم إعادة تعيين القوالب للافتراضي');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const handleSavePreferences = () => {
    if (SettingsService.savePreferences(preferences)) {
      console.log('✅ Preferences saved to localStorage');
      console.log('📦 localStorage data:', localStorage.getItem('linkedin_post_generator_settings'));
      setSaveMessage('✅ تم حفظ التفضيلات بنجاح! ستبقى محفوظة عند تحديث الصفحة.');
      setTimeout(() => setSaveMessage(''), 5000);
    } else {
      setSaveMessage('❌ فشل في حفظ التفضيلات');
    }
  };

  const handleResetPreferences = () => {
    if (window.confirm('إعادة تعيين جميع التفضيلات للافتراضي؟')) {
      const defaultPrefs = SettingsService.getDefaultPreferences();
      setPreferences(defaultPrefs);
      SettingsService.savePreferences(defaultPrefs);
      setSaveMessage('تم إعادة تعيين التفضيلات للافتراضي');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const handleExportSettings = () => {
    SettingsService.exportSettings();
    setSaveMessage('تم تصدير الإعدادات بنجاح!');
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const handleImportSettings = (event) => {
    const file = event.target.files[0];
    if (file) {
      SettingsService.importSettings(file)
        .then((settings) => {
          setPrompts(settings.prompts);
          setPreferences(settings.preferences);
          setSaveMessage('تم استيراد الإعدادات بنجاح!');
          setTimeout(() => setSaveMessage(''), 3000);
        })
        .catch((error) => {
          setSaveMessage('فشل في استيراد الإعدادات: ' + error.message);
          setTimeout(() => setSaveMessage(''), 5000);
        });
    }
  };

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-3">
          <span className="bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-600 bg-clip-text text-transparent">
            الإعدادات
          </span>
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          تخصيص القوالب والتفضيلات وعرض معلومات النظام
        </p>
      </div>

      {/* Save Message */}
      {saveMessage && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 text-green-800 px-6 py-4 rounded-2xl shadow-lg animate-fadeIn">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            {saveMessage}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-2xl shadow-lg p-2 border border-gray-100">
        <nav className="flex gap-2">
          <button
            onClick={() => setActiveTab('prompts')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-300 ${
              activeTab === 'prompts'
                ? 'text-white shadow-lg'
                : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
            }`}
            style={activeTab === 'prompts' ? { background: 'linear-gradient(135deg, #0066cc 0%, #00b4d8 100%)' } : {}}
          >
            <FileText size={20} />
            قوالب التوليد
          </button>
          <button
            onClick={() => setActiveTab('preferences')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-300 ${
              activeTab === 'preferences'
                ? 'text-white shadow-lg'
                : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
            }`}
            style={activeTab === 'preferences' ? { background: 'linear-gradient(135deg, #0066cc 0%, #00b4d8 100%)' } : {}}
          >
            <SettingsIcon size={20} />
            التفضيلات الشخصية
          </button>
          <button
            onClick={() => setActiveTab('system')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-300 ${
              activeTab === 'system'
                ? 'text-white shadow-lg'
                : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
            }`}
            style={activeTab === 'system' ? { background: 'linear-gradient(135deg, #0066cc 0%, #00b4d8 100%)' } : {}}
          >
            <Database size={20} />
            معلومات النظام
          </button>
        </nav>
      </div>

      {/* Prompts Tab */}
      {activeTab === 'prompts' && (
        <div className="space-y-6">
          <Card>
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">تخصيص قوالب الذكاء الاصطناعي</h2>
                <p className="text-sm text-gray-600">
                  عدّل القوالب المستخدمة لتوليد وتحسين منشورات لينكد إن.
                  استخدم المتغيرات مثل {'{title}'}, {'{topics}'}, {'{tone}'}, {'{content}'}, إلخ.
                </p>
              </div>

              {/* Generation Prompt */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  قالب التوليد الرئيسي
                </label>
                <textarea
                  value={prompts.generation}
                  onChange={(e) => setPrompts({ ...prompts, generation: e.target.value })}
                  className="w-full h-64 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                  placeholder="أدخل القالب الرئيسي لتوليد المنشورات..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  يُستخدم هذا القالب عند إنشاء منشورات لينكد إن جديدة
                </p>
              </div>

              {/* Refinement Prompt */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  قالب تحسين المحتوى
                </label>
                <textarea
                  value={prompts.refinement}
                  onChange={(e) => setPrompts({ ...prompts, refinement: e.target.value })}
                  className="w-full h-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                  placeholder="أدخل القالب لتحسين المنشورات..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  يُستخدم هذا القالب عند النقر على &quot;تحسين المحتوى&quot; مع الملاحظات
                </p>
              </div>

              {/* Hashtag Prompt */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  قالب توليد الهاشتاقات
                </label>
                <textarea
                  value={prompts.hashtags}
                  onChange={(e) => setPrompts({ ...prompts, hashtags: e.target.value })}
                  className="w-full h-40 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                  placeholder="أدخل القالب لتوليد الهاشتاقات..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  يُستخدم هذا القالب لتوليد هاشتاقات لينكد إن المناسبة
                </p>
              </div>

              {/* Image Prompts */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-5 border-2 border-purple-200">
                <label className="block text-sm font-medium text-purple-900 mb-2 flex items-center gap-2">
                  <span className="text-2xl">🎨</span>
                  قالب توليد الصور (جديد!)
                </label>
                <p className="text-sm text-purple-800 mb-3">
                  أنشئ وصفات صور للذكاء الاصطناعي لإنشاء صور احترافية لمنشورات لينكد إن.
                </p>
                <textarea
                  value={prompts.imagePrompts || ''}
                  onChange={(e) => setPrompts({ ...prompts, imagePrompts: e.target.value })}
                  className="w-full h-48 px-3 py-2 border-2 border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
                  placeholder="أدخل القالب لتوليد وصفات الصور..."
                />
                <p className="text-xs text-purple-700 mt-2 font-medium">
                  💡 نصيحة: يُنشئ هذا وصفات جاهزة للنسخ لأدوات الصور بالذكاء الاصطناعي
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <Button onClick={handleSavePrompts} variant="primary">
                  <Save size={16} className="me-2" />
                  حفظ القوالب
                </Button>
                <Button onClick={handleResetPrompts} variant="outline">
                  <RotateCcw size={16} className="me-2" />
                  إعادة للافتراضي
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Preferences Tab */}
      {activeTab === 'preferences' && (
        <div className="space-y-6">
          <Card>
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">التفضيلات الشخصية</h2>
                <p className="text-sm text-gray-600">
                  حدد تفضيلاتك الافتراضية لتسريع إنشاء المنشورات
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Default Language */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    اللغة الافتراضية
                  </label>
                  <select
                    value={preferences.language}
                    onChange={(e) => setPreferences({ ...preferences, language: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="english">إنجليزي</option>
                    <option value="arabic">عربي</option>
                    <option value="mixed">مختلط (عربي + إنجليزي)</option>
                  </select>
                </div>

                {/* Default Tone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    النبرة الافتراضية
                  </label>
                  <select
                    value={preferences.tone}
                    onChange={(e) => setPreferences({ ...preferences, tone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="professional">احترافي</option>
                    <option value="friendly">ودي</option>
                    <option value="motivational">تحفيزي</option>
                    <option value="educational">تعليمي</option>
                    <option value="storytelling">قصصي</option>
                    <option value="thought_leadership">قيادة فكرية</option>
                    <option value="conversational">محادثاتي</option>
                  </select>
                </div>

                {/* Default Length */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    طول المنشور الافتراضي
                  </label>
                  <select
                    value={preferences.lengthType}
                    onChange={(e) => setPreferences({ ...preferences, lengthType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="short">قصير (100-200 كلمة)</option>
                    <option value="medium">متوسط (200-400 كلمة)</option>
                    <option value="long">طويل (400-600 كلمة)</option>
                  </select>
                </div>

                {/* Emoji Usage */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    استخدام الإيموجي
                  </label>
                  <select
                    value={preferences.emojiUsage}
                    onChange={(e) => setPreferences({ ...preferences, emojiUsage: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="none">بدون</option>
                    <option value="light">خفيف (1-2 إيموجي)</option>
                    <option value="moderate">متوسط (3-5 إيموجي)</option>
                    <option value="heavy">كثير (6+ إيموجي)</option>
                  </select>
                </div>

                {/* Hashtag Count */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    عدد الهاشتاقات المفضل
                  </label>
                  <input
                    type="number"
                    min="3"
                    max="10"
                    value={preferences.hashtagCount}
                    onChange={(e) => setPreferences({ ...preferences, hashtagCount: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* CTA Style */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    أسلوب دعوة التفاعل
                  </label>
                  <select
                    value={preferences.ctaStyle}
                    onChange={(e) => setPreferences({ ...preferences, ctaStyle: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="question">سؤال</option>
                    <option value="action">إجراء</option>
                    <option value="discussion">نقاش</option>
                  </select>
                </div>
              </div>

              {/* Favorite Topics */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  المواضيع والكلمات المفتاحية المفضلة
                </label>
                <textarea
                  value={preferences.favoriteTopics}
                  onChange={(e) => setPreferences({ ...preferences, favoriteTopics: e.target.value })}
                  className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="SAP S/4HANA، ERP، إدارة المشاريع، تطوير البرمجيات، القيادة..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  قائمة بالمواضيع التي تكتب عنها بشكل متكرر، مفصولة بفواصل. يمكن إدراجها بسرعة عند إنشاء المنشورات.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <Button onClick={handleSavePreferences} variant="primary">
                  <Save size={16} className="me-2" />
                  حفظ التفضيلات
                </Button>
                <Button onClick={handleResetPreferences} variant="outline">
                  <RotateCcw size={16} className="me-2" />
                  إعادة للافتراضي
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* System Tab */}
      {activeTab === 'system' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* System Information */}
            <Card>
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <SettingsIcon size={24} className="me-2 text-blue-600" />
                معلومات النظام
              </h2>

              <div className="space-y-4">
                {systemInfo.map((item, index) => (
                  <div key={index} className="flex items-center justify-between py-2">
                    <div className="flex items-center">
                      <div className="text-blue-600 me-3">
                        {item.icon}
                      </div>
                      <span className="text-gray-700 font-medium">{item.label}</span>
                    </div>
                    <span className="text-gray-900 bg-gray-100 px-3 py-1 rounded-lg text-sm font-mono">
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-500">
                  <p><strong>الإصدار:</strong> 1.0.0</p>
                  <p><strong>آخر تحديث:</strong> {new Date().toLocaleDateString('ar-SA')}</p>
                  <p><strong>البناء:</strong> تجريبي</p>
                </div>
              </div>
            </Card>

            {/* Import/Export */}
            <Card>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                النسخ الاحتياطي والاستعادة
              </h2>

              <div className="space-y-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-blue-900 mb-2">تصدير الإعدادات</h3>
                  <p className="text-sm text-blue-800 mb-3">
                    حمّل القوالب والتفضيلات المخصصة كملف JSON للنسخ الاحتياطي أو المشاركة.
                  </p>
                  <Button onClick={handleExportSettings} variant="outline" size="small">
                    <Download size={16} className="me-2" />
                    تصدير الإعدادات
                  </Button>
                </div>

                <div className="bg-green-50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-green-900 mb-2">استيراد الإعدادات</h3>
                  <p className="text-sm text-green-800 mb-3">
                    استعد إعداداتك من ملف JSON تم تصديره مسبقاً.
                  </p>
                  <label className="cursor-pointer">
                    <Button variant="outline" size="small" as="span">
                      <Upload size={16} className="me-2" />
                      استيراد الإعدادات
                    </Button>
                    <input
                      type="file"
                      accept=".json"
                      onChange={handleImportSettings}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
            </Card>
          </div>

          {/* Quick Actions */}
          <Card>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              إجراءات سريعة
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => window.open(`${config.staticUrl}/health`, '_blank')}
                className="p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-start"
              >
                <div className="text-blue-600 mb-2">
                  <Database size={24} />
                </div>
                <h3 className="font-medium text-gray-900">فحص حالة الخادم</h3>
                <p className="text-sm text-gray-600">التحقق من حالة الخادم الخلفي</p>
              </button>

              <button
                onClick={() => window.open(`${config.staticUrl}/api`, '_blank')}
                className="p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-start"
              >
                <div className="text-green-600 mb-2">
                  <Zap size={24} />
                </div>
                <h3 className="font-medium text-gray-900">توثيق API</h3>
                <p className="text-sm text-gray-600">عرض نقاط نهاية API المتاحة</p>
              </button>

              <button
                onClick={() => window.location.reload()}
                className="p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-start"
              >
                <div className="text-purple-600 mb-2">
                  <SettingsIcon size={24} />
                </div>
                <h3 className="font-medium text-gray-900">تحديث التطبيق</h3>
                <p className="text-sm text-gray-600">إعادة تحميل التطبيق بالكامل</p>
              </button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Settings;
